import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import HomeImageCard from "./HomeImageCard";
import CountdownCard from "./CountdownCard";
import BMCButtonCard from "./BMCButtonCard";
import InteractivePoll from "./InteractivePoll";

/**
 * Right-column widgets — the same configurable list that appears on
 * the homepage's right rail. Reused on blog post pages so visitors see
 * the same admin-controlled tiles in the sidebar there too.
 *
 * If `widgets` is passed as a prop, this component renders without
 * making its own API call — handy when the parent fetches once and
 * renders the widgets in two responsive slots (e.g. desktop sidebar +
 * mobile-below-comments) to avoid double-fetches.
 */
export default function RightColumnWidgets({ className = "", testId = "right-widgets", widgets: widgetsProp }) {
  const [fetched, setFetched] = useState(null);
  const [spotifyId, setSpotifyId] = useState(null);
  const allWidgets = widgetsProp ?? fetched ?? [];
  // Per-widget `enabled` toggle (set from Admin → Homepage widgets).
  // Treat missing/undefined as visible so existing widgets keep showing
  // after the field was introduced.
  const widgets = allWidgets.filter((w) => w && w.enabled !== false);

  useEffect(() => {
    if (widgetsProp !== undefined) return; // parent supplies the data
    api
      .get("/settings/homepage-right-column")
      .then((r) => setFetched(r.data?.widgets || []))
      .catch(() => setFetched([]));
  }, [widgetsProp]);

  useEffect(() => {
    const hasSpotify = widgets.some((w) => w && w.type === "spotify");
    if (hasSpotify && !spotifyId) {
      api
        .get("/settings/spotify")
        .then((r) => setSpotifyId(r.data?.playlist_id || null))
        .catch(() => setSpotifyId(null));
    }
  }, [widgets, spotifyId]);

  return (
    <aside className={className} data-testid={testId}>
      {widgets.map((w) => {
        if (w.type === "image") {
          return (
            <HomeImageCard
              key={w.id}
              image={{
                image_url: w.image_url,
                link_url: w.link_url,
                alt: w.alt,
                open_in_new_tab: w.open_in_new_tab,
              }}
            />
          );
        }
        if (w.type === "countdown") return <CountdownCard key={w.id} config={w} />;
        if (w.type === "bmc") return <BMCButtonCard key={w.id} config={w} />;
        if (w.type === "spotify") {
          if (!spotifyId) {
            // Match the real iframe's height so it doesn't jump the layout
            // once the playlist id resolves and the iframe mounts.
            return (
              <div
                key={w.id}
                className="flex items-center rounded-md border border-black/10 bg-white p-4"
                data-testid="spotify-widget-loading"
                style={{ height: w.height ?? 380 }}
              >
                <p className="text-xs text-[var(--brand-body)] italic">Loading Spotify playlist...</p>
              </div>
            );
          }
          return (
            <div
              key={w.id}
              className="overflow-hidden rounded-md border border-black/10 bg-white shadow-sm"
              data-testid="spotify-widget"
              style={{ height: w.height ?? 380 }}
            >
              <iframe
                title="Spotify playlist widget"
                src={`https://open.spotify.com/embed/playlist/${spotifyId}?utm_source=generator&theme=0`}
                width="100%"
                height={w.height ?? 380}
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ border: 0, display: "block" }}
              />
            </div>
          );
        }
        if (w.type === "poll" && w.poll_id) {
          return <InteractivePoll key={w.id} pollId={w.poll_id} className="shadow-sm" scrollableOptions />;
        }
        return null;
      })}
      {widgets.length === 0 ? (
        <div
          className="hidden rounded-md border border-dashed border-black/15 p-5 text-xs italic text-[var(--brand-body)] lg:block"
          data-testid={`${testId}-empty`}
        >
          Configure the right-column widgets in{" "}
          <Link to="/admin/homepage-widgets" className="underline">
            Admin → Homepage widgets
          </Link>
          .
        </div>
      ) : null}
    </aside>
  );
}
